import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import type { AutomationTemplateStatus } from '@neuroedge/shared';
import { Button, Card, PageState, Select, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useAuth } from '../../auth/AuthContext';
import {
  useAdminAutomationTemplate,
  useCreateAutomationTemplate,
  useUpdateAutomationTemplate,
} from '../../api/automation-templates';
import { useAdminAutomationFlow, useAdminAutomationFlows } from '../../api/automation-flows';
import {
  blankAction,
  blankForm,
  describeAutomation,
  editorErrorKey,
  formFromDefinition,
  knownReplyOptions,
  templateDefinitionFromForm,
  toPayload,
  validateTemplateDefinition,
  type AutomationFormState,
} from '../automations/automation-model';
import { box, muted } from '../automations/automation-form-parts';
import { AutomationDefinitionEditor } from '../automations/AutomationDefinitionEditor';
import { FlowPreviewPanel } from '../automations/builder/FlowPreviewPanel';

const KEY_RE = /^[a-z0-9_]+$/;

const STATUS_OPTIONS: { value: AutomationTemplateStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Active (visible to tenants)' },
  { value: 'ARCHIVED', label: 'Archived (hidden from tenants)' },
];

/** A new template starts as "a customer sends a message -> reply". */
function exampleDefinition(): AutomationFormState {
  const form = blankForm();
  return { ...form, actions: [{ ...blankAction('reply'), text: 'Hello! How can we help?' }] };
}

/** A platform flow a template's "Start a guided conversation" step runs (side panel). */
function AdminFlowPreview({
  flowKey,
  flows,
}: {
  flowKey: string;
  flows: { id: string; key: string; name: string }[];
}) {
  const item = flows.find((f) => f.key === flowKey);
  const flow = useAdminAutomationFlow(item?.id);
  if (!item) return null;
  return (
    <FlowPreviewPanel
      name={item.name}
      definition={flow.data?.definition}
      loading={flow.isPending}
      openHref={`/admin/automation-flows/${item.id}/edit`}
    />
  );
}

/**
 * Platform template library: a template's When / If / Then is built with
 * the same editor tenants use for their automations (visual by default,
 * form, or JSON) - minus what a template can't know: the channel (none),
 * a channel's approved WhatsApp templates (typed by name), and the pipeline
 * for "add as a lead" (the workspace picks it when using the template). The
 * server validates the result (`automationTemplateDefinition`) on save.
 */
export function AutomationTemplateAdminFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const { isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const existing = useAdminAutomationTemplate(id);
  const create = useCreateAutomationTemplate();
  const update = useUpdateAutomationTemplate(id ?? '');
  const flows = useAdminAutomationFlows({ pageSize: 200 });

  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState<AutomationTemplateStatus>('ACTIVE');
  const [definition, setDefinition] = useState<AutomationFormState>(exampleDefinition);
  const [jsonValid, setJsonValid] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEdit && existing.data) {
      const t = existing.data;
      setKey(t.key);
      setName(t.name);
      setDescription(t.description ?? '');
      setCategory(t.category);
      setStatus(t.status);
      setDefinition(formFromDefinition(blankForm(), t.definition) ?? exampleDefinition());
    }
  }, [isEdit, existing.data]);

  const flowItems = useMemo(
    () => (flows.data?.items ?? []).filter((f) => f.status === 'ACTIVE' && !f.tenantId),
    [flows.data],
  );
  const replyOptions = useMemo(
    () =>
      knownReplyOptions([
        {
          name: name || 'this template',
          actions: toPayload(definition).actions.map((a) => ({
            ...a,
            config: a.config as Record<string, unknown>,
          })),
        },
      ]),
    [definition, name],
  );
  const summary = useMemo(() => {
    const payload = toPayload(definition);
    return describeAutomation(
      {
        triggerTypes: definition.triggers,
        conditions: (payload.conditions ?? []).map((c) => ({ ...c, value: c.value ?? null })),
        actions: payload.actions.map((a) => ({
          ...a,
          config: a.config as Record<string, unknown>,
        })),
      },
      {
        flowName: (k) => flowItems.find((f) => f.key === k)?.name,
        buttonTitles: Object.fromEntries(replyOptions.map((o) => [o.id, o.title])),
      },
    );
  }, [definition, flowItems, replyOptions]);

  if (!isPlatformAdmin) return <Navigate to="/" replace />;
  if (isEdit && existing.isPending) return <PageState kind="loading" message="Loading template…" />;
  if (isEdit && existing.isError)
    return <PageState kind="error" message={existing.error.message} />;

  const busy = create.isPending || update.isPending;

  function clearFieldError(k: string) {
    setFieldErrors((f) => {
      if (!f[k]) return f;
      const { [k]: _removed, ...rest } = f;
      return rest;
    });
  }

  /** Mirrors the backend's `createAutomationTemplateBody` checks it's cheap to catch client-side. */
  function validate(): Record<string, string> {
    const errors: Record<string, string> = validateTemplateDefinition(definition);

    const trimmedKey = key.trim();
    if (trimmedKey.length < 1) errors.key = 'Enter a key.';
    else if (trimmedKey.length > 120) errors.key = 'Key must be 120 characters or fewer.';
    else if (!KEY_RE.test(trimmedKey)) {
      errors.key = 'Use lowercase letters, numbers, and underscores only (e.g. my_template).';
    }

    const trimmedName = name.trim();
    if (trimmedName.length < 1) errors.name = 'Enter a name.';
    else if (trimmedName.length > 200) errors.name = 'Name must be 200 characters or fewer.';

    if (description.trim().length > 2000) {
      errors.description = 'Description must be 2000 characters or fewer.';
    }

    const trimmedCategory = category.trim();
    if (trimmedCategory.length < 1) errors.category = 'Enter a category (e.g. SALES).';
    else if (trimmedCategory.length > 60)
      errors.category = 'Category must be 60 characters or fewer.';

    return errors;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!jsonValid) {
      setError('Fix the JSON first - or switch back to the visual builder.');
      toast.error('Fix the JSON first.');
      return;
    }

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      toast.error('Please fix the highlighted fields.');
      return;
    }

    const payload = {
      key: key.trim(),
      name: name.trim(),
      description: description.trim() || null,
      category: category.trim(),
      status,
      definition: templateDefinitionFromForm(definition) as never,
    };

    try {
      if (isEdit && id) {
        await update.mutateAsync(payload);
      } else {
        await create.mutateAsync(payload);
      }
      toast.success(isEdit ? 'Template updated.' : 'Template created.');
      navigate('/admin/automation-templates', { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          // `definition/actions/0/config/text` lands on the editor's `actions/0/text`.
          const mapped: Record<string, string> = {};
          for (const [k, message] of Object.entries(serverFieldErrors)) {
            mapped[editorErrorKey(k)] = message;
          }
          setFieldErrors(mapped);
          setError('Please fix the highlighted fields.');
          toast.error('Please fix the highlighted fields.');
        } else {
          setError(err.message);
          toast.error(err.message);
        }
      } else {
        const message =
          err instanceof ApiClientError && err.httpStatus < 500
            ? err.message
            : 'Could not save the template.';
        setError(message);
        toast.error(message);
      }
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 1200 }}
    >
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>
        {isEdit ? 'Edit template' : 'New template'}
      </h1>
      <Card>
        <div
          style={{
            display: 'grid',
            gap: 'var(--space-3)',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          }}
        >
          <TextField
            label="Key"
            placeholder="sales_pricing_enquiry"
            value={key}
            onChange={(e) => {
              setKey(e.target.value);
              clearFieldError('key');
            }}
            error={fieldErrors.key}
          />
          <TextField
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              clearFieldError('name');
            }}
            error={fieldErrors.name}
          />
          <TextField
            label="Category"
            placeholder="SALES"
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              clearFieldError('category');
            }}
            error={fieldErrors.category}
          />
          <Select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as AutomationTemplateStatus)}
            options={STATUS_OPTIONS}
          />
        </div>
        <div style={{ marginTop: 'var(--space-3)' }}>
          <TextField
            label="Description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              clearFieldError('description');
            }}
            error={fieldErrors.description}
          />
        </div>
      </Card>

      <AutomationDefinitionEditor
        form={definition}
        setForm={(update) => setDefinition(update)}
        errors={fieldErrors}
        setErrors={setFieldErrors}
        channels={[]}
        replyOptions={replyOptions}
        lookups={{
          templates: [],
          templatesLoading: false,
          flows: flowItems,
          pipelines: [],
          noPipeline: true,
          freeTemplateNames: true,
        }}
        newAction={blankAction}
        flowPreview={(k) => <AdminFlowPreview flowKey={k} flows={flowItems} />}
        onJsonValidityChange={setJsonValid}
      />

      <div
        role="status"
        aria-label="Summary"
        style={{
          ...box,
          background: 'var(--color-surface-raised)',
          borderColor: 'var(--color-primary)',
        }}
      >
        <strong style={muted}>In plain words</strong>
        <span>{summary}</span>
      </div>

      {error ? (
        <p className="ne-auth__error" role="alert">
          {error}
        </p>
      ) : null}
      <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
        <Button type="submit" disabled={busy}>
          {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create template'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate('/admin/automation-templates')}
        >
          Cancel
        </Button>
      </div>
      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/admin/automation-templates">← All templates</Link>
      </p>
    </form>
  );
}
